import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { useAuth } from "./lib/auth";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { SetupPage } from "./pages/SetupPage";
import { HomePage } from "./pages/HomePage";
import { SalesPage } from "./pages/SalesPage";
import { PaymentPage } from "./pages/PaymentPage";
import { InventoryPage } from "./pages/InventoryPage";
import { ProductCreatePage } from "./pages/ProductCreatePage";
import { ProductCatalogPage } from "./pages/ProductCatalogPage";
import { ProductLibraryPage } from "./pages/ProductLibraryPage";
import { ReceiveStockPage } from "./pages/ReceiveStockPage";
import { CashBookPage } from "./pages/CashBookPage";
import { MorePage } from "./pages/MorePage";
import { DailyClosingPage } from "./pages/DailyClosingPage";
import { AlertsPage } from "./pages/AlertsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { TaxPage } from "./pages/TaxPage";
import { PartnersPage } from "./pages/PartnersPage";
import { DebtPage } from "./pages/DebtPage";
import { ActivityPage } from "./pages/ActivityPage";
import { SettingsPage } from "./pages/SettingsPage";
import { UsersPage } from "./pages/UsersPage";
import { SalesHistoryPage } from "./pages/SalesHistoryPage";
import { SaleDetailPage } from "./pages/SaleDetailPage";
import { DataToolsPage } from "./pages/DataToolsPage";
import { DataHealthPage } from "./pages/DataHealthPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import { StoreBranchesPage } from "./pages/StoreBranchesPage";
import { SmartImportPage } from "./pages/SmartImportPage";
import { HelpPage } from "./pages/HelpPage";

function ProtectedLayout() {
  const auth = useAuth();
  if (!auth.ready) return <div className="boot-screen">Đang mở Sổ Kinh Doanh...</div>;
  if (!auth.authenticated) return <Navigate to="/login" replace />;
  return <AppShell />;
}

function RoleRoute({
  roles,
  children,
}: {
  roles: string[];
  children: ReactNode;
}) {
  const auth = useAuth();
  const role = auth.session?.user.role;
  if (!role || !roles.includes(role)) {
    return <Navigate to="/more" replace />;
  }
  return <>{children}</>;
}

export function App() {
  const auth = useAuth();
  return (
    <Routes>
      <Route path="/login" element={auth.authenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/signup" element={auth.authenticated ? <Navigate to="/" replace /> : <SignupPage />} />
      <Route
        path="/setup"
        element={
          import.meta.env.DEV
            ? (auth.authenticated ? <Navigate to="/" replace /> : <SetupPage />)
            : <Navigate to="/signup" replace />
        }
      />
      <Route element={<ProtectedLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/sales" element={<RoleRoute roles={["OWNER", "MANAGER", "STAFF"]}><SalesPage /></RoleRoute>} />
        <Route path="/sales/payment" element={<RoleRoute roles={["OWNER", "MANAGER", "STAFF"]}><PaymentPage /></RoleRoute>} />
        <Route path="/sales/history" element={<SalesHistoryPage />} />
        <Route path="/sales/:id" element={<SaleDetailPage />} />
        <Route path="/products" element={<ProductCatalogPage />} />
        <Route path="/products/library" element={<ProductLibraryPage />} />
        <Route path="/products/new" element={<RoleRoute roles={["OWNER", "MANAGER"]}><ProductCreatePage /></RoleRoute>} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/inventory/new" element={<Navigate to="/products/new" replace />} />
        <Route path="/inventory/receive" element={<RoleRoute roles={["OWNER", "MANAGER", "STAFF"]}><ReceiveStockPage /></RoleRoute>} />
        <Route path="/inventory/import" element={<RoleRoute roles={["OWNER", "MANAGER", "STAFF"]}><SmartImportPage /></RoleRoute>} />
        <Route path="/cash" element={<CashBookPage />} />
        <Route path="/closing" element={<RoleRoute roles={["OWNER", "MANAGER"]}><DailyClosingPage /></RoleRoute>} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/reports" element={<RoleRoute roles={["OWNER", "MANAGER", "ACCOUNTANT"]}><ReportsPage /></RoleRoute>} />
        <Route path="/tax" element={<RoleRoute roles={["OWNER", "MANAGER", "ACCOUNTANT"]}><TaxPage /></RoleRoute>} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/debts" element={<DebtPage />} />
        <Route path="/activity" element={<RoleRoute roles={["OWNER", "MANAGER", "ACCOUNTANT"]}><ActivityPage /></RoleRoute>} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/users" element={<RoleRoute roles={["OWNER", "MANAGER"]}><UsersPage /></RoleRoute>} />
        <Route path="/data-tools" element={<RoleRoute roles={["OWNER", "MANAGER", "ACCOUNTANT"]}><DataToolsPage /></RoleRoute>} />
        <Route path="/data-health" element={<RoleRoute roles={["OWNER", "MANAGER", "ACCOUNTANT"]}><DataHealthPage /></RoleRoute>} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/branches" element={<StoreBranchesPage />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/help" element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
